"use client";

import {
  ConvertToPersianNumbers,
  ConvertToShamsiDate,
  FormatNumberWithSeparator,
  copyAdShareLink,
} from "@/utils/utils";
import { Tag, Share2 } from "lucide-react";
import { IconButton } from "@/components/icon-button";
import { useUserStore } from "@/store/userStore";
import { shouldShow } from "@/types/utils";

interface FileHeaderProps {
  title: string;
  adid: string;
  propertyType: string;
  listingType: string;
  totalPrice: string;
  colleaguePrice: string;
  minPrice: string;
  maxPrice: string;
  mortgageAmount: string;
  rentAmount: string;
  createdAt: string;
  maxMortgageAmount: string;
  minMortgageAmount: string;
  maxRentAmount: string;
  minRentAmount: string;
}

const TomanLabel = () => (
  <span className="font-normal text-base">تومان</span>
);

const formatPrice = (value: string) =>
  ConvertToPersianNumbers(FormatNumberWithSeparator(value));

export const FileHeader: React.FC<FileHeaderProps> = ({
  title,
  adid,
  propertyType,
  listingType,
  totalPrice,
  colleaguePrice,
  minPrice,
  maxPrice,
  mortgageAmount,
  rentAmount,
  createdAt,
  maxMortgageAmount,
  minMortgageAmount,
  maxRentAmount,
  minRentAmount,
}) => {
  const { is_colleague } = useUserStore();

  const handleShareClick = (e: React.MouseEvent<HTMLElement>) => {
    copyAdShareLink(title, adid, e);
  };

  return (
    <div className="w-full flex flex-col gap-3">
      <h1 className="text-[18px] sm:text-[20px] font-bold text-gray-900">
        {title}
      </h1>

      {/* Badges + created date */}
      <div className="flex flex-row items-center justify-between gap-2 min-w-0">
        <div className="flex items-center gap-2 w-fit min-w-0">
          <span className="bg-[#F97066] text-white px-2 py-1 rounded-sm text-sm w-fit shrink-0">
            {listingType}
          </span>
          <span className="bg-neutral-50 text-gray-700 px-2 py-1 rounded-sm text-sm md:text-lg font-medium w-fit shrink-0">
            {propertyType}
          </span>
        </div>

        {createdAt && (
          <span className="flex items-center gap-1 md:gap-4 text-gray-400 text-xs sm:text-base w-fit shrink-0">
            ایجاد: {ConvertToShamsiDate(createdAt)}
            <IconButton
              size="small"
              color="secondary"
              className="md:w-10 md:h-10"
              onClick={handleShareClick}>
              <Share2 className="size-5 md:size-7" />
            </IconButton>
            {/* temp: disabled till the function is implemented */}
            {/* <IconButton color="secondary">
              <Bookmark className="size-4 md:size-7" />
            </IconButton> */}
          </span>
        )}
      </div>

      {/* Price section */}
      <div className="flex flex-col md:flex-row md:items-center md:flex-wrap mt-1 gap-3 min-w-0">
          {/* Total Price */}
          {shouldShow(totalPrice) && (
            <div className="flex gap-2 items-center">
              <span className="flex items-center gap-1 text-gray-600 font-bold text-sm sm:text-base">
                <Tag size={18} className="sm:w-5 sm:h-5" /> قیمت
              </span>
              <span className="text-blue-800 font-bold text-base sm:text-lg">
                {formatPrice(totalPrice)} <TomanLabel />
              </span>
            </div>
          )}

          {/* Min / Max Price */}
          {!shouldShow(totalPrice) && (shouldShow(minPrice) || shouldShow(maxPrice)) && (
            <div className="flex gap-2 items-center">
              <span className="flex items-center gap-1 text-sm sm:text-base text-gray-600 font-bold">
                <Tag size={18} className="sm:w-5 sm:h-5" /> قیمت
              </span>
              <span className="text-blue-800 font-bold text-base sm:text-lg">
                {[
                  shouldShow(minPrice) ? formatPrice(minPrice) : null,
                  shouldShow(maxPrice) ? formatPrice(maxPrice) : null,
                ].filter(Boolean).join(" - ")} <TomanLabel />
              </span>
            </div>
          )}

          {/* Colleague price */}
          {is_colleague && shouldShow(colleaguePrice) &&
            <div className="flex gap-2 items-center">
              <span className="flex items-center gap-1 text-gray-500 text-sm sm:text-base">
                <Tag size={18} className="sm:w-5 sm:h-5" /> قیمت همکار
              </span>
              <span className="text-blue-800 font-bold text-base sm:text-lg">
                {formatPrice(colleaguePrice)} <TomanLabel />
              </span>
            </div>
          }

          {/* Mortgage */}
          {shouldShow(mortgageAmount) && (
            <div className="flex gap-2 items-center">
              <span className="flex items-center gap-1 text-gray-600 font-bold text-sm sm:text-base">
                <Tag size={18} className="sm:w-5 sm:h-5" /> رهن
              </span>
              <span className="text-blue-800 font-bold text-base sm:text-lg">
                {formatPrice(mortgageAmount)} <TomanLabel />
              </span>
            </div>
          )}

          {(shouldShow(maxMortgageAmount) || shouldShow(minMortgageAmount)) && (
            <div className="flex gap-2 items-center">
              <span className="flex items-center gap-1 font-bold text-sm sm:text-base text-gray-600">
                <Tag size={18} className="sm:w-5 sm:h-5" /> ودیعه
              </span>
              <span className="text-blue-800 font-bold text-base sm:text-lg">
                {[
                  shouldShow(minMortgageAmount) ? formatPrice(minMortgageAmount) : null,
                  shouldShow(maxMortgageAmount) ? formatPrice(maxMortgageAmount) : null,
                ].filter(Boolean).join(" - ")} <TomanLabel />
              </span>
            </div>
          )}

          {/* Rent */}
          {shouldShow(rentAmount) && (
            <div className="flex gap-2 items-center">
              <span className="flex items-center gap-1 text-gray-600 text-sm sm:text-base font-bold">
                <Tag size={18} className="sm:w-5 sm:h-5" /> اجاره
              </span>
              <span className="text-blue-800 font-bold text-base sm:text-lg">
                {formatPrice(rentAmount)} <TomanLabel />
              </span>
            </div>
          )}

          {(shouldShow(maxRentAmount) || shouldShow(minRentAmount)) && (
            <div className="flex gap-2 items-center">
              <span className="flex items-center gap-1 text-gray-600 font-bold text-sm sm:text-base">
                <Tag size={18} className="sm:w-5 sm:h-5" /> ماهانه
              </span>
              <span className="text-blue-800 font-bold text-base sm:text-lg">
                {[
                  shouldShow(minRentAmount) ? formatPrice(minRentAmount) : null,
                  shouldShow(maxRentAmount) ? formatPrice(maxRentAmount) : null,
                ].filter(Boolean).join(" - ")} <TomanLabel />
              </span>
            </div>
          )}
      </div>
    </div>
  );
};
